import { motion, useReducedMotion } from 'framer-motion'
import type { ReactNode } from 'react'
import { Tooltip } from './Tooltip'

const stroke = (delay: number, duration = 0.6, fade = false) => ({
  rest: { pathLength: 1, opacity: 1 },
  hover: {
    pathLength: [0, 1],
    ...(fade ? { opacity: [0, 1] } : {}),
    transition: { duration, ease: 'easeOut' as const, delay },
  },
})

/** A line of a draw icon; it redraws itself while the button around it is hovered. `delay` staggers the lines. */
export function DrawPath({
  d,
  delay = 0,
  duration = 0.6,
  fade = false,
}: {
  d: string
  delay?: number
  duration?: number
  /** Fade in while drawing, for a small piece such as an arrowhead. */
  fade?: boolean
}) {
  return <motion.path d={d} variants={stroke(delay, duration, fade)} />
}

/** A rectangle of a draw icon, redrawn like `DrawPath`. */
export function DrawRect({
  x,
  y,
  width,
  height,
  rx = 0,
  delay = 0,
}: {
  x: number
  y: number
  width: number
  height: number
  rx?: number
  delay?: number
}) {
  return (
    <motion.rect
      x={x}
      y={y}
      width={width}
      height={height}
      rx={rx}
      variants={stroke(delay)}
    />
  )
}

/**
 * An icon, and optionally a word, that reads as part of the page rather than as a button: nothing is filled on hover,
 * the icon redraws itself (as the dashboard's Review Deployments does) and both turn to the accent colour. Build the
 * icon from `DrawPath` / `DrawRect` on a 24×24 grid. A button, not an anchor, so the browser shows no address in the
 * corner when it is hovered; the hint is the app's own `Tooltip`, never the browser's `title`.
 */
export function DrawIconButton({
  icon,
  label,
  tooltip,
  tooltipSide = 'bottom',
  ariaLabel,
  onClick,
}: {
  icon: ReactNode
  /** Shown beside the icon from the `sm` breakpoint up. */
  label?: string
  /** The hint under the button; also its accessible name unless `ariaLabel` is given. */
  tooltip: string
  tooltipSide?: 'top' | 'bottom' | 'right'
  ariaLabel?: string
  onClick: () => void
}) {
  const reduceMotion = useReducedMotion()
  return (
    <Tooltip label={tooltip} side={tooltipSide} delay={400}>
      <motion.button
        type="button"
        aria-label={ariaLabel ?? tooltip}
        onClick={onClick}
        initial="rest"
        {...(reduceMotion
          ? {}
          : { whileHover: 'hover', whileTap: { scale: 0.96 } })}
        className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg px-1.5 text-sm font-medium text-secondary transition-colors hover:text-accent focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:outline-none"
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          className="size-[18px]"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {icon}
        </svg>
        {label && <span className="hidden sm:inline">{label}</span>}
      </motion.button>
    </Tooltip>
  )
}

/** An open book: both pages are drawn again on hover. */
export const docsDrawIcon = (
  <>
    <DrawPath d="M2 4h7a3 3 0 0 1 3 3v14a2 2 0 0 0-2-2H2z" />
    <DrawPath d="M22 4h-7a3 3 0 0 0-3 3v14a2 2 0 0 1 2-2h8z" delay={0.15} />
  </>
)

/** A sphere (ForgeSphere): the outline, then the meridian and the equator across it. */
export const platformDrawIcon = (
  <>
    <DrawPath d="M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18z" duration={0.7} />
    <DrawPath
      d="M12 3c2.5 2.4 4 5.6 4 9s-1.5 6.6-4 9c-2.5-2.4-4-5.6-4-9s1.5-6.6 4-9z"
      delay={0.35}
    />
    <DrawPath d="M3 12h18" delay={0.6} duration={0.4} />
  </>
)
